// game/enemies.ts — role-archetype brains, parameterised by EnemyDef.ai.
// Roles are the design unit (DESIGN §4): swarm / grunt / ranged / support / tank /
// stalker. Bodies differ per zone; behaviour lives here once.
//
// Every brain runs on the same spine: flow field toward the player + local
// separation steering, with a role-specific state machine on top. Every attack
// telegraphs (windup pose + flash + sound) — counterplay is never removed.

import type { EliteRider, EnemyDef, Vec } from '../types'
import type { Rng } from '../engine/rng'
import { TUNING } from '../data/tuning'
import { drawSprite } from '../engine/sprites'
import { emit } from '../engine/particles'
import type { EnemyEnt } from './entities'
import { TILE } from './world'
import type { Run } from './game'

const S_APPROACH = 0
const S_WINDUP = 1
const S_ATTACK = 2
const S_RECOVER = 3

const flowDir: Vec = { x: 0, y: 0 }
const sepDir: Vec = { x: 0, y: 0 }

function num(ai: EnemyDef['ai'], key: string, d: number): number {
  const v = ai[key]
  return typeof v === 'number' ? v : d
}

// ---------------------------------------------------------------- spawn setup

export function configureEnemy(
  e: EnemyEnt,
  def: EnemyDef,
  statScale: { hp: number; damage: number },
  elite: EliteRider | null,
  rng: Rng,
): void {
  e.reset()
  e.def = def
  e.alive = true
  e.maxHp = Math.max(1, Math.round(def.hp * statScale.hp))
  e.hp = e.maxHp
  e.radius = def.radius
  e.speed = def.speed
  e.touchDamage = def.touchDamage * statScale.damage
  e.spriteId = def.id
  e.scale = 1
  e.elite = elite
  e.spawnGrace = 0.25
  e.cool = rng.range(0.4, 1.0) // seeded: a run must replay identically

  if (elite === 'bloated') {
    e.maxHp = Math.round(e.maxHp * TUNING.elites.bloated.hpMul)
    e.hp = e.maxHp
    e.speed *= TUNING.elites.bloated.speedMul
    e.scale = 1.35
    e.radius = def.radius * 1.3
  } else if (elite === 'frenzied') {
    e.speed *= TUNING.elites.frenzied.speedMul
  } else if (elite === 'shielded') {
    e.shielded = true
  }
}

export function eliteTint(elite: EliteRider | null): string | undefined {
  switch (elite) {
    case 'frenzied':
      return '#ff5a3c'
    case 'shielded':
      return '#6fc3ff'
    case 'gloomtouched':
      return '#a06bff'
    case 'bloated':
      return '#7ad07a'
    default:
      return undefined
  }
}

// ---------------------------------------------------------------- steering

function separation(run: Run, e: EnemyEnt, out: Vec): void {
  out.x = 0
  out.y = 0
  const list = run.enemyPool.live
  const reach = e.radius * 2.4
  run.hash.query(e.pos.x, e.pos.y, reach, (i) => {
    const o = list[i]
    if (o === e || !o.alive) return
    const dx = e.pos.x - o.pos.x
    const dy = e.pos.y - o.pos.y
    const d2 = dx * dx + dy * dy
    const want = e.radius + o.radius
    if (d2 >= want * want) return
    if (d2 < 0.0001) {
      out.x += 0.7
      out.y -= 0.3
      return
    }
    const d = Math.sqrt(d2)
    const push = (want - d) / want
    out.x += (dx / d) * push
    out.y += (dy / d) * push
  })
}

export function moveEnemy(run: Run, e: EnemyEnt, vx: number, vy: number, dt: number): void {
  // knockback decays fast and is additive to intent
  const k = Math.pow(0.0016, dt)
  e.kbx *= k
  e.kby *= k
  if (Math.abs(e.kbx) < 1) e.kbx = 0
  if (Math.abs(e.kby) < 1) e.kby = 0
  const dx = (vx + e.kbx) * dt
  const dy = (vy + e.kby) * dt
  const px = e.pos.x
  const py = e.pos.y
  run.world.moveCircle(e.pos, dx, dy, e.radius)
  e.vel.x = vx
  e.vel.y = vy
  if (Math.abs(vx) > 6) e.facing = vx < 0 ? -1 : 1

  // Anti-wedge (D-015): the per-tile flow field is radius-unaware, so a body
  // straddling a tile row can be pinned against a wall corner with non-zero
  // intent and zero displacement, forever. When that happens, nudge the body
  // toward its tile centre on the axis orthogonal to the dominant intent, and
  // count the stuck time so the room-clear backstop can see it.
  const intent = Math.abs(dx) + Math.abs(dy)
  const moved = Math.abs(e.pos.x - px) + Math.abs(e.pos.y - py)
  if (intent > 0.5 && moved < 0.05) {
    e.stuckT += dt
    const tcx = Math.floor(e.pos.x / TILE) * TILE + TILE / 2
    const tcy = Math.floor(e.pos.y / TILE) * TILE + TILE / 2
    if (Math.abs(dx) >= Math.abs(dy)) {
      const s = Math.sign(tcy - e.pos.y) || 1
      run.world.moveCircle(e.pos, 0, s * Math.max(30, Math.abs(vx)) * dt, e.radius)
    } else {
      const s = Math.sign(tcx - e.pos.x) || 1
      run.world.moveCircle(e.pos, s * Math.max(30, Math.abs(vy)) * dt, 0, e.radius)
    }
  } else if (moved >= 0.05) {
    e.stuckT = 0
  }
}

/** Flow field toward the player, falling back to straight-line when unreachable. */
export function pursue(run: Run, e: EnemyEnt, out: Vec): void {
  if (!run.world.flowAt(e.pos.x, e.pos.y, out)) {
    const dx = run.player.pos.x - e.pos.x
    const dy = run.player.pos.y - e.pos.y
    const l = Math.hypot(dx, dy) || 1
    out.x = dx / l
    out.y = dy / l
  }
}

// ---------------------------------------------------------------- brains

/**
 * Custom brain registry (Phase 6 extension point). A zone's content file
 * registers its own AI under a key, and any EnemyDef with `ai.custom: '<key>'`
 * runs that brain instead of the role default. Registration lives with the
 * zone's content, so shared code never learns zone-specific behaviour.
 */
export type EnemyBrain = (run: Run, e: EnemyEnt, dt: number, speedMul: number) => void
const CUSTOM_BRAINS = new Map<string, EnemyBrain>()

export function registerEnemyBrain(key: string, brain: EnemyBrain): void {
  CUSTOM_BRAINS.set(key, brain)
}

/** Exported for custom brains that want the stock behaviours as building blocks. */
export function roleBrain(role: string): EnemyBrain {
  switch (role) {
    case 'swarm': return brainSwarm
    case 'ranged': return brainRanged
    default: return brainGrunt
  }
}

export function updateEnemy(run: Run, e: EnemyEnt, dt: number): void {
  if (!e.alive) return
  if (e.hitFlashT > 0) e.hitFlashT -= dt
  if (e.telegraph > 0) e.telegraph -= dt
  if (e.spawnGrace > 0) e.spawnGrace -= dt
  if (e.cool > 0) e.cool -= dt
  e.bob += dt
  e.stateT += dt

  // Stagger (Bulwark Maul, and anything else that wants it): `tags.stunT` holds
  // the seconds left. A stagger interrupts a windup — that is the whole point of
  // the heavy form — and drops the body into recovery, so it re-approaches
  // cleanly when it comes to. Knockback still carries it: moveEnemy(0,0) applies
  // and decays the impulse without adding intent.
  //
  // `tags.stunProof` (husks): a body with no verbs cannot be interrupted, and
  // its brain carries passive contracts (the walk-over seal, the reform clock)
  // that a stun must never silence — a staggered husk with a paused brain is a
  // husk the Sexton's Spade cannot seal (D-031). Stun-immune bodies shed the
  // stun and fall through to their brain.
  const stun = typeof e.tags.stunT === 'number' ? e.tags.stunT : 0
  if (stun > 0) {
    if (e.tags.stunProof === true) {
      e.tags.stunT = 0
    } else {
      e.tags.stunT = stun - dt
      e.state = S_RECOVER
      e.stateT = 0
      e.windup = 0
      e.telegraph = 0
      moveEnemy(run, e, 0, 0, dt)
      return
    }
  }

  const speedMul = run.gloomSys.enemySpeedMul()

  const custom = typeof e.def.ai.custom === 'string' ? CUSTOM_BRAINS.get(e.def.ai.custom) : undefined
  if (custom) {
    custom(run, e, dt, speedMul)
    return
  }

  switch (e.def.role) {
    case 'swarm':
      brainSwarm(run, e, dt, speedMul)
      break
    case 'ranged':
      brainRanged(run, e, dt, speedMul)
      break
    case 'grunt':
    case 'tank':
    case 'support':
    case 'stalker':
    default:
      brainGrunt(run, e, dt, speedMul)
      break
  }
}

// --- swarm: pack lunges. One lunge cues the neighbours, so they arrive together.

function brainSwarm(run: Run, e: EnemyEnt, dt: number, speedMul: number): void {
  const ai = e.def.ai
  const lungeRange = num(ai, 'lungeRange', 92)
  const lungeCd = num(ai, 'lungeCooldown', 2.1)
  const windup = num(ai, 'windup', 0.26)
  const lungeSpeed = num(ai, 'lungeSpeed', 360)
  const lungeTime = num(ai, 'lungeTime', 0.24)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy)

  separation(run, e, sepDir)

  if (e.state === S_APPROACH) {
    pursue(run, e, flowDir)
    const sp = e.speed * speedMul
    moveEnemy(
      run,
      e,
      (flowDir.x + sepDir.x * 1.5) * sp,
      (flowDir.y + sepDir.y * 1.5) * sp,
      dt,
    )
    if (
      dist < lungeRange &&
      e.cool <= 0 &&
      e.spawnGrace <= 0 &&
      run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
    ) {
      e.state = S_WINDUP
      e.stateT = 0
      e.windup = windup
      e.telegraph = windup
      run.sfx('telegraph')
      // pack cue: nearby packmates commit at the same time
      const list = run.enemyPool.live
      run.hash.query(e.pos.x, e.pos.y, 110, (i) => {
        const o = list[i]
        if (o === e || !o.alive || o.def.role !== 'swarm') return
        if (o.state === S_APPROACH && o.cool > 0.15) o.cool *= 0.35
      })
    }
  } else if (e.state === S_WINDUP) {
    e.windup -= dt
    // crouch: drift slightly back, telegraph reads as "about to spring"
    moveEnemy(run, e, -dx * 0.25 + sepDir.x * 40, -dy * 0.25 + sepDir.y * 40, dt)
    if (e.windup <= 0) {
      e.state = S_ATTACK
      e.stateT = 0
      const l = dist || 1
      e.kbx = (dx / l) * lungeSpeed
      e.kby = (dy / l) * lungeSpeed
      emit(e.pos.x, e.pos.y, { count: 5, color: '#8a7358', speed: 70, life: 0.22, size: 2 })
    }
  } else if (e.state === S_ATTACK) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > lungeTime) {
      e.state = S_RECOVER
      e.stateT = 0
    }
  } else {
    pursue(run, e, flowDir)
    const sp = e.speed * 0.5 * speedMul
    moveEnemy(run, e, (flowDir.x + sepDir.x) * sp, (flowDir.y + sepDir.y) * sp, dt)
    if (e.stateT > 0.35) {
      e.state = S_APPROACH
      e.stateT = 0
      e.cool = lungeCd
    }
  }
}

// --- grunt: advance, then a telegraphed poke. Line infantry.

function brainGrunt(run: Run, e: EnemyEnt, dt: number, speedMul: number): void {
  const ai = e.def.ai
  const reach = num(ai, 'attackRange', 30)
  const cd = num(ai, 'attackCooldown', 1.7)
  const windup = num(ai, 'windup', 0.45)
  const strikeArc = num(ai, 'strikeArc', 1.5)
  const strikeRange = num(ai, 'strikeRange', 42)
  const dmgMul = num(ai, 'strikeDamage', 1)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy)
  separation(run, e, sepDir)

  if (e.state === S_APPROACH) {
    pursue(run, e, flowDir)
    const sp = e.speed * speedMul
    moveEnemy(run, e, (flowDir.x + sepDir.x * 1.4) * sp, (flowDir.y + sepDir.y * 1.4) * sp, dt)
    if (dist < reach + run.player.radius + e.radius && e.cool <= 0 && e.spawnGrace <= 0) {
      e.state = S_WINDUP
      e.stateT = 0
      e.windup = windup
      e.telegraph = windup
      run.sfx('telegraph')
    }
  } else if (e.state === S_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, sepDir.x * 30, sepDir.y * 30, dt)
    if (e.windup <= 0) {
      e.state = S_ATTACK
      e.stateT = 0
      const dir = Math.atan2(dy, dx)
      const pdx = run.player.pos.x - e.pos.x
      const pdy = run.player.pos.y - e.pos.y
      const pd = Math.hypot(pdx, pdy)
      let inArc = pd <= strikeRange + run.player.radius + e.radius
      if (inArc && pd > 2) {
        let a = Math.atan2(pdy, pdx) - dir
        while (a > Math.PI) a -= Math.PI * 2
        while (a < -Math.PI) a += Math.PI * 2
        inArc = Math.abs(a) <= strikeArc / 2
      }
      if (inArc) run.damagePlayer(e.touchDamage * dmgMul, e.def.id)
      run.sfx('swing')
      emit(e.pos.x + Math.cos(dir) * 20, e.pos.y + Math.sin(dir) * 20, {
        count: 4,
        color: '#cfd6e4',
        speed: 110,
        life: 0.16,
        size: 2,
        angle: dir,
        spread: strikeArc,
      })
    }
  } else if (e.state === S_ATTACK) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > 0.12) {
      e.state = S_RECOVER
      e.stateT = 0
    }
  } else {
    moveEnemy(run, e, sepDir.x * 40, sepDir.y * 40, dt)
    if (e.stateT > 0.4) {
      e.state = S_APPROACH
      e.stateT = 0
      e.cool = cd
    }
  }
}

// --- ranged: hold a band, break line, fire on a telegraph.

function brainRanged(run: Run, e: EnemyEnt, dt: number, speedMul: number): void {
  const ai = e.def.ai
  const keep = num(ai, 'keepRange', 210)
  const minRange = num(ai, 'minRange', 130)
  const fireRange = num(ai, 'fireRange', 320)
  const cd = num(ai, 'fireCooldown', 2.4)
  const windup = num(ai, 'windup', 0.55)
  const projId = typeof ai.projectile === 'string' ? ai.projectile : ''

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const los = run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
  separation(run, e, sepDir)

  if (e.state === S_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, sepDir.x * 25, sepDir.y * 25, dt)
    if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1
    if (e.windup <= 0) {
      e.state = S_RECOVER
      e.stateT = 0
      e.cool = cd
      if (projId) {
        run.spawnProjectileAt(projId, e.pos.x, e.pos.y - 6, dx / dist, dy / dist, false, px, py)
        run.sfx('shoot')
      }
    }
    return
  }

  if (e.state === S_RECOVER) {
    moveEnemy(run, e, sepDir.x * 30, sepDir.y * 30, dt)
    if (e.stateT > 0.35) {
      e.state = S_APPROACH
      e.stateT = 0
    }
    return
  }

  // reposition: too close = back off (uses reverse flow), too far = close in
  const sp = e.speed * speedMul
  let mx = 0
  let my = 0
  if (dist < minRange || !los) {
    if (dist < minRange) {
      mx = -(dx / dist)
      my = -(dy / dist)
    } else {
      pursue(run, e, flowDir)
      mx = flowDir.x
      my = flowDir.y
    }
  } else if (dist > keep) {
    pursue(run, e, flowDir)
    mx = flowDir.x
    my = flowDir.y
  } else {
    // strafe to stay awkward to aim at
    const s = e.bob * 0.7 + e.pos.x * 0.01
    mx = (-dy / dist) * Math.sin(s)
    my = (dx / dist) * Math.sin(s)
  }
  moveEnemy(run, e, (mx + sepDir.x * 1.3) * sp, (my + sepDir.y * 1.3) * sp, dt)
  if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1

  if (e.cool <= 0 && e.spawnGrace <= 0 && dist < fireRange && los) {
    e.state = S_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
}

// ---------------------------------------------------------------- render

export function renderEnemy(ctx: CanvasRenderingContext2D, e: EnemyEnt): void {
  if (!e.alive) return
  const lunging = e.def.role === 'swarm' && e.state === S_ATTACK
  const crouch = e.telegraph > 0 ? Math.min(1, e.telegraph / 0.3) : 0
  const bob = Math.sin(e.bob * 9) * (e.def.role === 'swarm' ? 1.2 : 0.6)

  ctx.globalAlpha = 0.32
  ctx.fillStyle = '#000000'
  ctx.beginPath()
  ctx.ellipse(e.pos.x, e.pos.y + e.radius * 0.75, e.radius * 0.9, e.radius * 0.4, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.globalAlpha = 1

  // telegraph ring — the tell survives every difficulty tier
  if (e.telegraph > 0) {
    const k = 1 - Math.min(1, e.telegraph / 0.6)
    ctx.globalAlpha = 0.28 + 0.4 * k
    ctx.strokeStyle = '#ffd166'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(e.pos.x, e.pos.y, e.radius + 7 - k * 4, 0, Math.PI * 2)
    ctx.stroke()
    ctx.globalAlpha = 1
  }

  const tint = eliteTint(e.elite)
  drawSprite(ctx, e.spriteId, e.pos.x, e.pos.y - 2 + bob - crouch * 2, {
    flip: e.facing < 0,
    scale: e.scale * (lunging ? 1.12 : 1),
    white: e.hitFlashT > 0 ? 1 : e.telegraph > 0 ? 0.28 : 0,
    tint,
  })

  // staggered: two pale motes over the head, so a maul hit reads as a state
  const stunned = typeof e.tags.stunT === 'number' && e.tags.stunT > 0
  if (stunned) {
    const t = e.bob * 6
    ctx.globalAlpha = 0.85
    ctx.fillStyle = '#cfe4ff'
    ctx.fillRect(Math.round(e.pos.x + Math.cos(t) * 7 - 1), Math.round(e.pos.y - e.radius - 14), 3, 3)
    ctx.fillRect(Math.round(e.pos.x + Math.cos(t + 2.1) * 7 - 1), Math.round(e.pos.y - e.radius - 10), 2, 2)
    ctx.globalAlpha = 1
  }

  if (e.elite === 'shielded' && e.shielded) {
    ctx.globalAlpha = 0.5
    ctx.strokeStyle = '#6fc3ff'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.arc(e.pos.x, e.pos.y, e.radius + 5, 0, Math.PI * 2)
    ctx.stroke()
    ctx.globalAlpha = 1
  }

  if (e.hp < e.maxHp) {
    const w = Math.max(16, e.radius * 2.2)
    const x = e.pos.x - w / 2
    const y = e.pos.y - e.radius - 11
    ctx.fillStyle = '#00000099'
    ctx.fillRect(x - 1, y - 1, w + 2, 4)
    ctx.fillStyle = e.elite ? '#ffb44a' : '#d8503c'
    ctx.fillRect(x, y, w * Math.max(0, e.hp / e.maxHp), 2)
  }
}
